import { Box, Divider, Grid, Tab, Tabs, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { CheckoutState, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AddOnItemName, UpgradeDialogSource } from "schemas/dashboard";

import StyledPage from "components/Styled/Page";
import { PAGE_VISITED } from "constants/trackingProps";
import Subscription from "features/Home/UpgradeDialog/Subscription";
import { getParams, trackEvent } from "utils/tracking";

import AddOnCustomEmail from "./AddOns/AddOnsCustomEmail";
import AddOnsEmailVolume from "./AddOns/AddOnsEmailVolume";
import AddOnPartnershipData from "./AddOns/AddOnsPartnershipData";
import AddOnTemplate from "./AddOns/AddOnsTemplates";
import AffiliateProgram from "./AffiliateProgram";
import BrandAmbassador from "./BrandAmbassador";
import DedicatedSupport from "./DedicatedSupport";
import DeleteAccount from "./DeleteAccount";
import Integrations from "./Integrations";
import Referral from "./Referral";
import SubscriptionButton from "./SubscriptionButton";
import UserNotificationsSettings from "./UserNotifications";
import styles from "./styles";

export default function Settings() {
  const { currentOrg } = useContext(OrganizationUserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    setCustomDomainDialog,

    setUpgradeDialogSource,
    setOpenCheckoutDialog,
  } = useContext(SubscriptionContext);

  const scrollToSubscription = searchParams.get("section") === "subscription";
  const scrollToAddOn = searchParams.get("section") === "addon";
  const openEmailDialog = searchParams.get("custom-email") === "true";
  const openEmailVolume = searchParams.get("open") === "email_volume";
  const openTemplate = searchParams.get("ref") === "one_time_template_coupon";

  const subscriptionRef = useRef<HTMLDivElement>(null);
  const addOnRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const [referralTab, setReferralTab] = useState(0);

  const scrollToNotifications = searchParams.get("unsubscribe") === "true";

  const openEmailVolumeCheckout = () => {
    setUpgradeDialogSource(UpgradeDialogSource.email_volume_from_ps_email);
    const state: CheckoutState = {
      plan: AddOnItemName.email_volume_yearly,
      source: "Email Volume from P.S Email",
      pageSource: window.location.pathname,
    };
    setOpenCheckoutDialog(state);
    searchParams.delete("email_volume");
    setSearchParams(searchParams);
  };

  const openTemplateCheckout = () => {
    setUpgradeDialogSource(UpgradeDialogSource.free_template_coupon_email);
    const state: CheckoutState = {
      plan: AddOnItemName.pitching_templates,
      source: "Free Template Coupon Email",
      pageSource: window.location.pathname,
      coupon: getParams("coupon"),
    };
    setOpenCheckoutDialog(state);
    searchParams.delete("ref");
    setSearchParams(searchParams);
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollToSubscription && subscriptionRef.current) {
      subscriptionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [scrollToSubscription]);

  useEffect(() => {
    if (scrollToNotifications && notificationsRef.current) {
      notificationsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [scrollToNotifications]);

  useEffect(() => {
    if ((openEmailDialog || scrollToAddOn) && addOnRef.current) {
      addOnRef.current.scrollIntoView({ behavior: "smooth" });
    }
    if (currentOrg?.id && openEmailDialog) {
      setCustomDomainDialog(true);
    } else if (currentOrg?.id && openEmailVolume) {
      openEmailVolumeCheckout();
    } else if (currentOrg?.id && openTemplate) {
      openTemplateCheckout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentOrg?.id,
    scrollToAddOn,
    openEmailDialog,
    openEmailVolume,
    openTemplate,
  ]);

  return (
    <StyledPage
      content={
        <Grid sx={{ py: 3 }}>
          {!currentOrg?.isBrand && (
            <>
              <DedicatedSupport />

              <Grid
                container
                justifyContent={"space-between"}
                ref={subscriptionRef}
                sx={{ alignItems: "center", gap: 1, mb: 2 }}
              >
                <Typography variant="h2" sx={{ fontSize: { xs: 20, md: 40 } }}>
                  Pick Your Subscription Plan
                </Typography>{" "}
                <SubscriptionButton />
              </Grid>

              <Subscription upgradeDialogSource={null} />

              <Typography ref={addOnRef} variant="h4" gutterBottom>
                Add-Ons
              </Typography>

              <Grid container gap={1} sx={styles.addOnItemGridContainer}>
                {currentOrg?.isVaUser ? (
                  <AddOnsEmailVolume />
                ) : (
                  <AddOnPartnershipData />
                )}

                <AddOnCustomEmail />

                <AddOnTemplate />
              </Grid>

              <Tabs
                value={referralTab}
                onChange={(_, v) => setReferralTab(v)}
                sx={{ mb: 3, mt: 6, borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="Referral Program" />
                <Tab label="Brand Ambassador" />
                <Tab label="Affiliate Program" />
              </Tabs>
              {referralTab === 0 ? (
                <Referral />
              ) : referralTab === 1 ? (
                <BrandAmbassador />
              ) : (
                <AffiliateProgram />
              )}

              <Divider sx={{ my: 4 }} />
            </>
          )}

          <Box sx={{ my: 4 }}>
            <Integrations />
          </Box>
          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" gutterBottom ref={notificationsRef}>
            Notifications
          </Typography>

          <Grid item xs={12}>
            <UserNotificationsSettings />
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ my: 4 }}>
            <DeleteAccount />
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" color="text.secondary">
            To report a bug, please contact us at{" "}
            <strong>hello@onbento.com</strong>.
          </Typography>

          <Box sx={{ height: 50 }} />
        </Grid>
      }
    />
  );
}
